import type UserSession from '../model/UserSession'

// Kept in step with the fuller enum in model/User.ts, which is what drives the role dropdown
// and the permission map. The two have drifted before (model/User.ts also carries `manager` and
// `interpreter`); a role only needs to be here if something in this file gates on it.
export enum userRolesBase {
  doctor = 'doctor',
  nurse = 'nurse',
  medTech = 'medTech',
  socialWorker = 'social-worker',
  technicalAssistant = 'technical-assistant',
  specialist = 'specialist',
  supplyManager = 'supplyManager',
}

// Who runs the exam report: the people who order and perform exams. Doctors and nurses order them
// and follow up on the results, so they read the same report the medtech performs against.
export const canViewExaminationsReport = (userSession: UserSession): boolean =>
  userSession.admin ||
  [userRolesBase.medTech, userRolesBase.doctor, userRolesBase.nurse].includes(
    userSession.role as userRolesBase,
  )

/**
 * Who sees the supply counts.
 *
 * The supply manager owns the stock, and the medtech who files the counts should be able to
 * read back what they filed. See patient-backend/docs/supply-checklist.md.
 *
 * Spelled out rather than derived from `canViewExaminationsReport`: the two lists used to be the
 * same, but clinicians read the exam report without having any business in the stock counts.
 */
export const canViewSupplyReports = (userSession: UserSession): boolean =>
  userSession.admin ||
  [userRolesBase.medTech, userRolesBase.supplyManager].includes(userSession.role as userRolesBase)

/**
 * Who edits the checklist itself — which items the medtech is asked to count.
 *
 * Admins are included deliberately: someone has to be able to fix the list before the first
 * supply-manager account exists, and after it is locked out.
 */
export const canManageSupplyChecklist = (userSession: UserSession): boolean =>
  userSession.admin || userSession.role === userRolesBase.supplyManager

/**
 * Whether the /reports route and its sidebar entry render at all. A user who can reach any one
 * tab can reach the page; Reports.tsx decides which tabs they actually get.
 */
export const canViewReports = (userSession: UserSession): boolean =>
  canViewExaminationsReport(userSession) || canViewSupplyReports(userSession)

/**
 * A supply manager has no patient access at all, so the patients dashboard the app normally
 * lands on shows them "No Permission". HospitalRun redirects `/` for them instead.
 */
export const isSupplyManager = (userSession: UserSession): boolean =>
  userSession.role === userRolesBase.supplyManager
